// ── TrainingHero.jsx ──────────────────────────────────────────────────────────
// FIXES:
//  - Particle count reduced to 14 on mobile (was 28 always) via JS matchMedia
//  - Orbs get will-change: transform for GPU compositing
//  - Removed backdrop-filter from tp-pk-badge / tp-announce / tp-price-pill
//    (replaced with slightly stronger background — big paint savings)
//  - hero-actions stacking handled by CSS (tokens), inline flex-direction removed
//  - tp-hero padding-top reduced on mobile so content isn't clipped
import { useMemo } from "react";
import { DiscordIcon, ChevronRight } from "./TrainingShared";

/* ── Particle field ──────────────────────────────────────────────────────── */
function Particles() {
  const count = useMemo(() => {
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches) return 10;
    return 22;
  }, []);

  const particles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        width: `${2 + Math.random() * 3}px`,
        height: `${2 + Math.random() * 3}px`,
        bottom: `${Math.random() * 30}%`,
        background:
          i % 3 === 0
            ? "var(--tp-violet)"
            : i % 3 === 1
              ? "var(--tp-cyan)"
              : "var(--tp-blue)",
        animationDuration: `${6 + Math.random() * 10}s`,
        animationDelay: `${Math.random() * 8}s`,
      })),
    [count],
  );
  return (
    <div className="tp-particles" aria-hidden="true">
      {particles.map((p) => (
        <div
          key={p.id}
          className="tp-particle"
          style={{
            left: p.left,
            width: p.width,
            height: p.height,
            bottom: p.bottom,
            background: p.background,
            animationDuration: p.animationDuration,
            animationDelay: p.animationDelay,
          }}
        />
      ))}
    </div>
  );
}

/* ── Hero CSS ────────────────────────────────────────────────────────────── */
export const HERO_CSS = `
  .tp-hero {
    position: relative;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 8rem 1.5rem 5rem;
    contain: layout style;
  }
  .tp-hero::before {
    content: '';
    position: absolute; inset: 0;
    background:
      radial-gradient(ellipse 85% 55% at 18% 20%, rgba(77,127,255,.13) 0%, transparent 62%),
      radial-gradient(ellipse 55% 75% at 82% 78%, rgba(130,72,245,.1) 0%, transparent 62%),
      radial-gradient(ellipse 45% 38% at 58% 8%, rgba(24,212,240,.06) 0%, transparent 52%);
    pointer-events: none;
  }
  .tp-hero-grid {
    position: absolute; inset: 0;
    background-image:
      linear-gradient(rgba(77,127,255,.045) 1px, transparent 1px),
      linear-gradient(90deg, rgba(77,127,255,.045) 1px, transparent 1px);
    background-size: 56px 56px;
    mask-image: radial-gradient(ellipse 88% 88% at 50% 38%, black 0%, transparent 72%);
    animation: tp-grid-drift 22s linear infinite;
    will-change: background-position;
  }
  .tp-hero-noise {
    position: absolute; inset: 0;
    opacity: .025;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    background-size: 256px 256px;
    pointer-events: none;
  }
  .tp-orb {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    filter: blur(2px);
    will-change: transform;
  }
  .tp-orb-1 {
    width: 780px; height: 780px;
    background: radial-gradient(circle, rgba(77,127,255,.14) 0%, transparent 65%);
    top: -22%; left: -16%;
    animation: tp-float 10s ease-in-out infinite;
  }
  .tp-orb-2 {
    width: 620px; height: 620px;
    background: radial-gradient(circle, rgba(130,72,245,.11) 0%, transparent 65%);
    bottom: -14%; right: -9%;
    animation: tp-float 13s ease-in-out infinite reverse;
  }
  .tp-orb-3 {
    width: 320px; height: 320px;
    background: radial-gradient(circle, rgba(24,212,240,.07) 0%, transparent 65%);
    top: 33%; right: 13%;
    animation: tp-float 8s ease-in-out infinite 1.5s;
  }
  /* Hide largest orbs on mobile — major perf win */
  @media (max-width: 768px) {
    .tp-orb-1 { width: 400px; height: 400px; }
    .tp-orb-2 { width: 320px; height: 320px; }
    .tp-orb-3 { display: none; }
  }
  .tp-particles {
    position: absolute; inset: 0;
    pointer-events: none; overflow: hidden;
  }
  .tp-particle {
    position: absolute;
    border-radius: 50%;
    opacity: 0;
    animation: tp-particle-rise linear infinite;
    will-change: transform, opacity;
  }
  .tp-hero-inner {
    position: relative;
    max-width: 860px;
    text-align: center;
    z-index: 1;
    width: 100%;
  }
  .tp-heo-inner-div {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }
  /* Pakistan badge — backdrop-filter removed, stronger bg instead */
  .tp-pk-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 14px 5px 7px;
    background: rgba(255,255,255,.065);
    border: 1px solid rgba(255,255,255,.09);
    border-radius: 100px;
    font-size: .68rem;
    font-weight: 700;
    color: var(--tp-t2);
    margin-bottom: 11px;
    letter-spacing: .1em;
    text-transform: uppercase;
    animation: tp-fadein .45s both;
  }
  .tp-pk-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--tp-green);
    box-shadow: 0 0 8px var(--tp-green);
    animation: tp-pulse-dot 2s ease-in-out infinite;
  }
  /* Announce pill */
  .tp-announce {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 5px 16px 5px 8px;
    background: rgba(77,127,255,.12);
    border: 1px solid rgba(77,127,255,.22);
    border-radius: 100px;
    font-size: .76rem;
    color: var(--tp-t2);
    margin-bottom: 1.5rem;
    animation: tp-fadein .5s .06s both;
  }
  .tp-announce-chip {
    padding: 2px 10px;
    background: linear-gradient(135deg, var(--tp-blue), var(--tp-violet));
    border-radius: 100px;
    font-size: .65rem;
    font-weight: 700;
    color: #fff;
    letter-spacing: .04em;
    text-transform: uppercase;
  }
  /* H1 */
  .tp-h1 {
    font-family: var(--tp-display);
    font-size: clamp(3.2rem, 9vw, 6.5rem);
    font-weight: 700;
    letter-spacing: -.05em;
    line-height: .96;
    color: var(--tp-t1);
    margin: 0 0 1.5rem;
    animation: tp-fadein .6s .12s both;
  }
  .tp-h1-gradient {
    background: linear-gradient(135deg, var(--tp-blue) 0%, var(--tp-violet) 48%, var(--tp-pink) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    display: block;
  }
  .tp-sub {
    font-size: clamp(1rem, 2.2vw, 1.175rem);
    color: var(--tp-t2);
    line-height: 1.85;
    max-width: 600px;
    margin: 0 auto 2rem;
    animation: tp-fadein .6s .2s both;
  }
  /* Price pill */
  .tp-price-pill {
    display: inline-flex;
    align-items: center;
    gap: .875rem;
    padding: .7rem 1.875rem;
    background: rgba(245,158,11,.07);
    border: 1px solid rgba(245,158,11,.18);
    border-radius: 100px;
    margin-bottom: 2.5rem;
    animation: tp-fadein .6s .28s both;
    flex-wrap: wrap;
    justify-content: center;
  }
  .tp-price-lbl  { font-size: .65rem; color: var(--tp-t3); letter-spacing: 2.5px; text-transform: uppercase; font-family: var(--tp-mono); }
  .tp-price-num  { font-size: 1.65rem; font-weight: 700; color: var(--tp-gold); }
  .tp-price-div  { color: rgba(245,158,11,.28); font-size: 1.25rem; }
  /* CTA row — direction handled by responsive CSS in tokens */
  .tp-hero-actions {
    display: flex;
    align-items: center;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
    animation: tp-fadein .6s .36s both;
    width: 100%;
  }

  /* Enrollment button wrapper */
  .tp-enroll-wrap {
    position: relative;
    display: inline-flex;
  }
  .tp-enroll-ring {
    position: absolute;
    inset: -6px;
    border-radius: 20px;
    border: 1.5px solid rgba(77,127,255,.3);
    animation: tp-glow-ring 2.4s ease-in-out infinite;
    pointer-events: none;
    will-change: box-shadow;
  }

  /* Scroll hint */
  .tp-scroll-hint {
    position: absolute;
    bottom: 2.25rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: var(--tp-t3);
    font-size: .65rem;
    letter-spacing: .14em;
    text-transform: uppercase;
    animation: tp-fadein .8s .75s both;
    font-family: var(--tp-mono);
  }
  @media (max-width: 480px) { .tp-scroll-hint { display: none; } }
  .tp-scroll-line {
    width: 1px; height: 38px;
    background: linear-gradient(to bottom, var(--tp-t3), transparent);
    animation: tp-scroll-pulse 2s ease-in-out infinite;
  }
  /* Trusted bar */
  .tp-trusted {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 3.5rem;
    animation: tp-fadein .6s .45s both;
  }
  .tp-trusted-avatars { display: flex; }
  .tp-trusted-av {
    width: 34px; height: 34px;
    border-radius: 50%;
    border: 2px solid var(--tp-bg);
    margin-left: -10px;
    font-size: .95rem;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .tp-trusted-av:first-child { margin-left: 0; }
  .tp-trusted-text { font-size: .82rem; color: var(--tp-t2); }
  .tp-trusted-text strong { color: var(--tp-t1); }

  /* Discord secondary label in hero */
  .tp-hero-discord-label {
    font-size: .72rem;
    color: var(--tp-t3);
    margin-top: .625rem;
    text-align: center;
    letter-spacing: .04em;
  }

  /* Mobile hero padding fix */
  @media (max-width: 640px) {
    .tp-hero { padding: 6rem 1rem 4rem; min-height: 100svh; }
    .tp-h1   { line-height: 1.0; }
    .tp-price-pill { gap: .5rem; padding: .6rem 1.25rem; }
    .tp-price-div  { display: none; }
  }
`;

export default function TrainingHero({ scrollToPayment }) {
  return (
    <section className="tp-hero">
      <div className="tp-hero-grid" aria-hidden="true" />
      <div className="tp-hero-noise" aria-hidden="true" />
      <div className="tp-orb tp-orb-1" aria-hidden="true" />
      <div className="tp-orb tp-orb-2" aria-hidden="true" />
      <div className="tp-orb tp-orb-3" aria-hidden="true" />
      <Particles />

      <div className="tp-hero-inner">
        <div className="tp-heo-inner-div">
          <div className="tp-pk-badge">
            <span style={{ fontSize: "1rem" }}>🇵🇰</span>
            <span className="tp-pk-dot" />
            Pakistan's Largest Open Source Community
          </div>
          <div className="tp-announce">
            <span className="tp-announce-chip">New Cohort</span>
            Applications open — limited seats available
          </div>
        </div>

        <h1 className="tp-h1">
          Build Real.
          <br />
          <span className="tp-h1-gradient">Ship Real.</span>
          Get Hired.
        </h1>

        <p className="tp-sub">
          Pakistan's most intensive dev program. One month of hands-on training
          on live QuantumLogics products — then a free 3-month internship to
          launch your career.
        </p>

        <div className="tp-price-pill">
          <span className="tp-price-lbl">Total Cost</span>
          <span className="tp-price-div">|</span>
          <span className="tp-price-num">₨2,000</span>
          <span className="tp-price-div">|</span>
          <span className="tp-price-lbl">Internship Free</span>
        </div>

        <div className="tp-hero-actions">
          <div className="tp-enroll-wrap">
            <div className="tp-enroll-ring" aria-hidden="true" />
            <button
              className="tp-btn-primary tp-btn-enroll-hero"
              onClick={scrollToPayment}
            >
              Enroll for ₨2,000
              <ChevronRight size={16} />
            </button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <a
              href="https://discord.gg/qCvxh7ZhSv"
              target="_blank"
              rel="noopener noreferrer"
              className="tp-btn-discord"
            >
              <DiscordIcon size={16} />
              Join Discord — Free
            </a>
            <span className="tp-hero-discord-label">10,000+ community members</span>
          </div>
        </div>

        <div className="tp-trusted">
          <div className="tp-trusted-avatars">
            {["👨‍💻", "👩‍💻", "🧑‍💻", "👨‍🔬", "🧑‍🎨", "👩‍🔬"].map((e, i) => (
              <div
                key={i}
                className="tp-trusted-av"
                style={{ background: `hsl(${i * 44 + 215}, 55%, 16%)` }}
              >
                {e}
              </div>
            ))}
          </div>
          <p className="tp-trusted-text">
            Joined by <strong>10,000+</strong> devs across Pakistan
          </p>
        </div>
      </div>

      <div className="tp-scroll-hint" aria-hidden="true">
        <div className="tp-scroll-line" />
        scroll
      </div>
    </section>
  );
}
